import React, { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import InfitiveScrollDropown from '~/components/InfitiveScrollDropown'
import FarmerGroupQuery from '~/QueryHooks/FarmerGroupsQuery'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { formatNumber } from '~/utils/helpers'
import FormSection from './FormSection'

const BasicFarmerInfo: React.FC = () => {
  const { t } = useTranslation()
  const { control, getValues, setValue } = useFormContext()

  const watchParentId = useWatch({ control, name: 'parentId' })
  const watchFarmerType = useWatch({ control, name: 'farmerType' })
  const watchMilkTypeId = useWatch({ control, name: 'milkTypeId' })

  const {
    data: farmerOptions,
    isLoading: isLoadingOptions,
    refetch: fetchOptions
  } = FarmersQuery.getFarmerOptions()

  const {
    data: farmersList,
    isLoading: isLoadingFarmers
  } = FarmersQuery.getFarmers({ isMain: 1, page: 0, search: '' })

  const {
    data: farmerGroupList,
    isLoading: isLoadingFarmerGrouplist
  } = FarmerGroupQuery.getFarmersList()

  useEffect(() => {
    fetchOptions()
  }, [t])

  const milkTypeOption = farmerOptions?.milkTypes.find(milkType => milkType.id === watchMilkTypeId)
  const showOrganicCertification = Boolean(milkTypeOption?.isBio)

  useEffect(() => {
    if (!showOrganicCertification) {
      setValue('organicCertificationNumber', '')
    }
  }, [watchMilkTypeId])

  function renderEmptyFields() {
    return <>
      <Form.Field></Form.Field>
      <Form.Field></Form.Field>
    </>
  }

  const isSubFarmer = (watchFarmerType === 'sub' || !!watchParentId)

  return <>
    <FormSection id='basic-information' header={t('Basic Information')} headerIcon='building'>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          name={'evsNumber'}
          placeholder={t('EVS number')}
          rules={{
            required: t('Please enter evs')
          }}
          label={t('EVS number')}
        />
        <FormFields.Input
          control={control}
          name={'supplierNo'}
          placeholder={t('Suplier no.')}
          rules={{
            required: t('Please enter supplier number')
          }}
          label={t('Suplier no.')}
        />
        <FormFields.Input
          control={control}
          name={'agisId'}
          placeholder={t('AGIS-ID Operation')}
          rules={{
            required: t('Please enter agis-id')
          }}
          label={t('AGIS-ID Operation')}
        />
        <FormFields.Select
          selectOnBlur={false}
          control={control}
          label={t('Farmer group')}
          disabled={isLoadingFarmerGrouplist}
          loading={isLoadingFarmerGrouplist}
          options={farmerGroupList?.map(farmerGroup => ({
            value: farmerGroup.id,
            text: `${farmerGroup.name} (${formatNumber(farmerGroup.amountPercent)}%)`
          })) || []}
          name='farmerGroupId'
          placeholder={t('Select farmer group')}
          rules={{
            required: {
              value: true,
              message: t('Select farmer group')
            }
          }}
        />

      </Form.Group>

      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          name={'companyName'}
          rules={{
            required: {
              value: true,
              message: t('Plese enter company name')
            }
          }}
          placeholder={t('Company Name')}
          label={t('Company Name')}
        />

        <FormFields.Checkbox
          control={control}
          defaultValue={0}
          style={{ marginTop: 35 }}
          checkboxValue={1}
          name={'deliveryStop'}
          label={t('Delivery stop')}
        />
      </Form.Group>
    </FormSection>

    <FormSection id='managers-certifications' header={t('Managers & Certifications')} headerIcon='users'>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          disabled={isLoadingOptions}
          rules={{
            required: t('Please select managers')
          }}
          placeholder={t('Select manager')}
          multiple
          label={t('Assigned Managers')}
          search
          name={'managers'}
          options={farmerOptions?.managers?.map(manager => ({ value: manager.id, text: `${manager.name} ${manager.agisId ? `(${manager.agisId})` : ''}` })) || []}
        />
        <FormFields.Select
          control={control}
          placeholder={t('Please select certification')}
          label={t('Certifications')}
          name={'certifications'}
          multiple
          options={farmerOptions?.certifications?.map(certification => ({ value: certification.id, text: certification.name }))}
        />
        <FormFields.Select
          control={control}
          rules={{
            required: t('Please select contract type')
          }}
          placeholder={t('Please select contract type')}
          label={t('Contract Type')}
          name={'contractTypeId'}
          options={farmerOptions?.contractTypes?.map(contractType => ({ value: contractType.id, text: contractType.label }))}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'farmerType'}
          label={t('Farmer type')}
          scrolling
          selectOnBlur={false}
          rules={{
            required: {
              value: true,
              message: t('Please select farmer type')
            }
          }}
          placeholder={t('Select farmer type')}
          options={farmerOptions?.farmerTypes?.map(farmerType => ({ value: farmerType.key, text: farmerType.label })) || []}
        />
        {isSubFarmer ? <InfitiveScrollDropown
          control={control}
          selectOnBlur={false}
          name={'parentId'}
          label={t('Main farmer')}
          value={getValues('parentId')}
          disabled={isLoadingFarmers}
          rules={{
            required: {
              value: true,
              message: t('Please select main farmer')
            }
          }}
          setValue={setValue}
          placeholder={t('Select main farmer')}
          options={farmersList.map(farmer => ({ value: farmer.id, text: farmer.companyName })) || []}
        /> : renderEmptyFields()}
      </Form.Group>
    </FormSection>

    <FormSection id='production' header={t('Production')} headerIcon='factory'>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          selectOnBlur={false}
          control={control}
          label={t('Milk Type')}
          name={'milkTypeId'}
          rules={{
            required: t('Please select milk type')
          }}
          clearable
          placeholder={t('Select milk type')}
          options={farmerOptions?.milkTypes?.map(milkType => (
            {
              value: milkType?.id,
              text: milkType?.name
            }
          )) || []}
        />
        {showOrganicCertification && (
          <FormFields.Input
            control={control}
            name={'organicCertificationNumber'}
            placeholder={t('Organic Certification Number')}
            label={t('Organic Certification #')}
            noErrorMessage
            rules={{
              required: true
            }}
          />
        )}
        <FormFields.Select
          name={'milkTypeCharacteristicId'}
          selectOnBlur={false}
          control={control}
          label={t('Milk Characteristics')}
          clearable
          placeholder={t('Select milk characteristics')}
          options={farmerOptions?.milkTypeCharacteristics?.map(milkType => (
            {
              value: milkType?.id,
              text: milkType?.label
            }
          )) || []}
        />
        <FormFields.Select
          name={'milkPurchase'}
          selectOnBlur={false}
          control={control}
          label={t('Milk Purchase')}
          rules={{
            required: t('Please select milk purchase')
          }}
          clearable
          placeholder={t('Select milk purchase')}
          options={farmerOptions?.milkPurchases?.map(milkPurchase => (
            {
              value: milkPurchase?.key,
              text: milkPurchase?.label
            }
          )) || []}
        />
      </Form.Group>

      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          rules={{
            required: t('Please select production zone')
          }}
          placeholder={t('Please select production zone')}
          label={t('Production Zone')}
          name={'productionZoneId'}
          options={farmerOptions?.productionZones?.map(productionZone => ({ value: productionZone.id, text: productionZone.name }))}
        />
        <FormFields.Select
          control={control}
          rules={{
            required: t('Please select operating form')
          }}
          placeholder={t('Please select operating form')}
          label={t('Operating Form')}
          name={'operatingFormId'}
          options={farmerOptions?.operatingForms?.map(operatingForm => ({ value: operatingForm.id, text: operatingForm.name }))}
        />
      </Form.Group>
    </FormSection>

    <FormSection id='collection-transport' header={t('Collection & Transport')} headerIcon='truck'>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          rules={{
            required: t('Please select collection cycle')
          }}
          placeholder={t('Please select collection cycle')}
          label={t('Collection Cycle')}
          name={'collectionCycleId'}
          options={farmerOptions?.collectionCycles
            ?.filter(collectionCycle => Boolean(collectionCycle?.name))
            .map(collectionCycle => ({
              value: collectionCycle.id,
              text: collectionCycle.name
            }))}
        />
        <FormFields.Select
          selectOnBlur={false}
          control={control}
          label={t('Collection Method')}
          name={'collectionMethodId'}
          clearable
          placeholder={t('Select collection method')}
          options={farmerOptions?.collectionMethods?.map(method => ({ value: method.id, text: method.label }))}
        />
        <FormFields.Select
          selectOnBlur={false}
          control={control}
          label={t('Transport Area')}
          name={'transportAreaId'}
          rules={{
            required: t('Please select transport area')
          }}
          clearable
          placeholder={t('Select transport area')}
          options={farmerOptions?.transportAreas?.map(transportArea => (
            {
              value: transportArea?.id,
              text: transportArea?.name
            }
          )) || []}
        />
      </Form.Group>

      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'restrictionPeriodId'}
          label={t('Restriction Period')}
          selectOnBlur={false}
          placeholder={t('Select restriction period')}
          options={farmerOptions?.restrictionPeriods?.map(restrictionPeriod => ({ text: restrictionPeriod.name, value: restrictionPeriod.id }))}
        />
        <FormFields.Select
          control={control}
          selectOnBlur={false}
          label={t('Collection Points')}
          name={'collectionPoints'}
          search
          clearable
          multiple
          placeholder={t('Select collection points')}
          options={farmerOptions?.collectionPoints?.map(point => (
            {
              value: point?.id,
              text: point?.name
            }
          )) || []}
        />
        <FormFields.Select
          control={control}
          selectOnBlur={false}
          label={t('Collection Sites')}
          name={'collectionSites'}
          search
          clearable
          multiple
          placeholder={t('Select collection sites')}
          options={farmerOptions?.collectionSites?.map(site => (
            {
              value: site?.id,
              text: site?.name
            }
          )) || []}
        />
      </Form.Group>
    </FormSection>

  </>
}
export default BasicFarmerInfo